import type { MetricCatalogEntry, SuggestionLever, SuggestionUnit } from '@monitoring-panel/types';
import { formatDecimal } from '@/lib/utils';

/**
 * Number formatting of the delivery report, as the skill's HTML template did
 * it: European separators, a space before the unit, an en dash for no value.
 */

export const NO_VALUE = '–';

type Value = number | null | undefined;

function isMissing(value: Value): value is null | undefined {
  return value === null || value === undefined || Number.isNaN(value);
}

export function num(value: Value, decimals = 0): string {
  return isMissing(value) ? NO_VALUE : formatDecimal(value, decimals);
}

export function eur(value: Value, decimals = 2): string {
  return isMissing(value) ? NO_VALUE : `${formatDecimal(value, decimals)} €`;
}

/** The value is already a percentage (12,5 means 12,5 %). */
export function pct(value: Value, decimals = 1): string {
  return isMissing(value) ? NO_VALUE : `${formatDecimal(value, decimals)} %`;
}

/** Headroom is a ratio of impressions held back to impressions won. */
export function times(value: Value): string {
  return isMissing(value) ? NO_VALUE : `${formatDecimal(value, 1)}×`;
}

/** `metrics_catalog[id].fmt` is `int`, or `eur`/`pct` followed by the decimals (eur2, pct1…). */
export function formatMetric(
  catalog: Record<string, MetricCatalogEntry>,
  metricId: string,
  value: Value
): string {
  if (isMissing(value)) return NO_VALUE;
  const fmt = catalog[metricId]?.fmt ?? 'eur2';
  if (fmt === 'int') return num(value);
  const decimals = Number(fmt.slice(-1));
  const safeDecimals = Number.isNaN(decimals) ? 2 : decimals;
  return fmt.startsWith('pct') ? pct(value, safeDecimals) : eur(value, safeDecimals);
}

/** "YYYY-MM-DD" → "dd/mm". */
export function dayMonth(isoDate: string | null | undefined): string {
  if (!isoDate) return NO_VALUE;
  return `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}`;
}

/** "YYYY-MM-DD" → "dd/mm/yyyy", without going through Date (no timezone shift). */
export function fullDate(isoDate: string | null | undefined): string {
  if (!isoDate) return NO_VALUE;
  return `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}/${isoDate.slice(0, 4)}`;
}

/** ISO timestamp → "19/10/2026, 16:54" in Madrid time. */
export function dateTime(isoTimestamp: string | null | undefined): string {
  if (!isoTimestamp) return NO_VALUE;
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return isoTimestamp;
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Madrid',
  });
}

/** Levers whose missing `before` means the entity had no cap at all. */
const CAP_LEVERS: readonly SuggestionLever[] = ['daily_cap', 'io_daily_cap'];

/** A suggestion's before/after value in its unit: €, € CPM or impressions. */
export function formatSuggestionValue(
  unit: SuggestionUnit | null,
  value: number | null,
  lever: SuggestionLever
): string {
  if (value === null) return CAP_LEVERS.includes(lever) ? 'sin tope' : NO_VALUE;
  switch (unit) {
    case 'EUR':
      return eur(value, Number.isInteger(value) ? 0 : 2);
    case 'EUR_CPM':
      return `${eur(value, 2)} CPM`;
    case 'IMPRESSIONS':
      return `${num(value)} impr.`;
    default:
      return num(value, 2);
  }
}

/** Suffix for an input typed in the suggestion's unit. */
export function unitSuffix(unit: SuggestionUnit | null): string {
  switch (unit) {
    case 'EUR':
      return '€';
    case 'EUR_CPM':
      return '€ CPM';
    case 'IMPRESSIONS':
      return 'impr.';
    default:
      return '';
  }
}

/** Dots grouping thousands with no decimal comma: "8.300", "1.250.000". */
const THOUSANDS_ONLY = /^\d{1,3}(\.\d{3})+$/;

/** Parses a number typed the Spanish way ("1.500,5", "8.300") or the dotted way ("1500.5"). */
export function parseLocaleNumber(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const isSpanish = trimmed.includes(',') || THOUSANDS_ONLY.test(trimmed);
  const normalised = isSpanish ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
  const value = Number(normalised);
  return Number.isFinite(value) ? value : null;
}
